import { capitalize, Chip } from '@mui/material';
import { BlockStatus } from '@strength/domain';
import { I18nTranslator, TKey } from '@strength/i18n';
import { MdCheck, MdClose, MdHorizontalRule } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';

export type DisplayWorkoutStatus =
  | { type: 'draft' }
  | { type: 'planned' }
  | { type: 'unpublished-changes' }
  | { type: 'in-progress'; readOnly: boolean }
  | {
      type: 'completed';
      completed?: number;
      toComplete?: number;
      readOnly?: boolean;
    }
  | { type: 'skipped' }
  | { type: 'canceled' };

export const isWorkoutStatusReadOnly = (status: DisplayWorkoutStatus) => {
  const { type } = status;
  if (type === 'in-progress' || type === 'completed') {
    return status.readOnly;
  }

  return (
    type !== 'planned' && type !== 'draft' && type !== 'unpublished-changes'
  );
};

/**
 * Whether drag interactions are blocked for a workout status: read-only
 * statuses, plus in-progress and completed workouts. A started workout's
 * logged history belongs to its original date, so drag must not move it —
 * even though in-progress stays editable in the builder (readOnly is
 * false). Completed blocks regardless of readOnly: the plan calendar
 * blocks the completed layer status outright, and the All view matches —
 * including the multi-athlete completed workout that stays editable. The
 * drag surfaces warn at drag start and reject the drop.
 */
export const isWorkoutStatusDragBlocked = (status: DisplayWorkoutStatus) =>
  isWorkoutStatusReadOnly(status) ||
  status.type === 'in-progress' ||
  status.type === 'completed';

/**
 * The i18n key explaining why a workout's status blocks the drag — null
 * when the status allows the move. The drag-start warning explains the
 * reason instead of only saying "can't be moved".
 */
export const getWorkoutStatusDragBlockedReasonKey = (
  status: DisplayWorkoutStatus,
): TKey | null => {
  if (!isWorkoutStatusDragBlocked(status)) return null;
  switch (status.type) {
    case 'in-progress':
      return 'workoutCannotBeMovedInProgress';
    case 'completed':
      return 'workoutCannotBeMovedCompleted';
    case 'skipped':
      return 'workoutCannotBeMovedSkipped';
    case 'canceled':
      return 'workoutCannotBeMovedCanceled';
    default:
      return 'workoutCannotBeMoved';
  }
};

export const WorkoutStatusBadge = ({
  status,
  blockStatus,
  short = false,
}: {
  status: DisplayWorkoutStatus;
  blockStatus?: BlockStatus;
  short?: boolean;
}) => {
  const { t } = useTranslation();
  const { type } = status;
  // if the block is canceled, show canceled unless the status is completed
  if (blockStatus === 'canceled' && status.type !== 'completed') {
    status.type = 'canceled';
  }
  if (type === 'planned') {
    return null;
  }

  return (
    <Chip
      label={!short ? getLabelForStatus(status, t) : undefined}
      sx={{
        height: 18,
        '.MuiChip-label': {
          py: 1,
          px: 1,
          fontSize: 11,
        },
        ...(short && {
          '.MuiChip-label': {
            display: 'none',
          },
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          padding: 0,
          '& .MuiChip-icon': {
            margin: 0,
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'inherit',
          },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }),
      }}
      className={getStyleForStatus(type)}
      icon={short ? getIconForStatus(status) : undefined}
      title={getLabelForStatus(status, t)}
    />
  );
};

const getLabelForStatus = (status: DisplayWorkoutStatus, t: I18nTranslator) => {
  if (status.type === 'in-progress') {
    return t('inProgress');
  }
  if (status.type === 'completed') {
    if (status.completed === status.toComplete) {
      return t('completed');
    }
    return t('completedCount', {
      completed: status.completed,
      toComplete: status.toComplete,
    });
  }
  if (status.type === 'unpublished-changes') {
    return t('unpublishedChanges');
  }
  return capitalize(status.type);
};

const getIconForStatus = (status: DisplayWorkoutStatus) => {
  if (status.type === 'completed') {
    return <MdCheck size={10} />;
  }
  if (status.type === 'canceled') {
    return <MdClose size={10} />;
  }

  if (status.type === 'skipped') {
    return <MdHorizontalRule size={10} />;
  }

  return undefined;
};

const getStyleForStatus = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100/50 text-green-800 font-bold';
    case 'canceled':
      return 'bg-red-100/50 text-red-700 font-bold';
    case 'in-progress':
      return 'bg-blue-100/50 text-blue-800 font-bold';
    case 'skipped':
      return 'bg-orange-100/50 text-orange-800 font-bold';
    default:
      return 'bg-gray-100 text-gray-800 font-bold';
  }
};
